import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdEdit, MdDelete } from 'react-icons/md';
import { FaMapMarkerAlt, FaChevronRight, FaFilePdf } from 'react-icons/fa';
import { getApiEndpoint } from '../../../config/api';
import './PropertyCard.css';
import placeholderImage from '../../../assets/placeholder.png';

const STATE_PILL = {
  good:     'COMPLIANT',
  info:     'REVIEW',
  warning:  'INVESTIGATE',
  critical: 'ACTION REQUIRED',
  unknown:  'NO DATA',
};

const PropertyCard = ({ property, onDelete, onPropertyClick, viewOnly }) => {
  const [imageError, setImageError] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  useEffect(() => { setImageError(false); }, [property.image_url]);
  const navigate = useNavigate();
  const showActions = !viewOnly && (onDelete != null);

  const getImageUrl = (imageUrl) => {
    if (!imageUrl || imageError) return placeholderImage;
    if (imageUrl.startsWith('http')) return imageUrl;
    if (imageUrl.startsWith('uploads/')) {
      return getApiEndpoint(`/api/${imageUrl}`);
    }
    return getApiEndpoint(`/api/uploads/${imageUrl}`);
  };

  const handleClick = (e) => {
    if (e.target.closest('.property-actions') || e.target.closest('.confirmation-modal')) {
      return;
    }
    onPropertyClick(property.id);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    navigate(`/property/edit/${property.id}`, { state: { property } });
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowConfirmDelete(true);
  };

  const handleReport = (e) => {
    e.stopPropagation();
    const today = new Date().toISOString().slice(0, 10);
    navigate(`/properties/${property.id}/reports/${today}`);
  };

  // New compliance-state-driven look (from /api/properties/compliance-summary).
  const state = property.compliance_state || 'unknown';
  const pct = property.compliance_pct ?? 0;
  const totalAssets = property.total_assets ?? 0;
  const failed = property.failed_count ?? 0;
  const dueSoon = property.pending_count ?? 0;
  const untested = property.untested_count ?? 0;

  const shortAddress = property.address
    ? property.address.split(',').slice(0, 2).join(',')
    : 'No address';

  return (
    <>
      <div
        className={`property-card pc-state-${state}`}
        onClick={handleClick}
        tabIndex={0}
        role="button"
        aria-label={`Open ${property.name}`}
      >
        {/* Thumbnail */}
        <div className="pc-thumb">
          <img
            src={getImageUrl(property.image_url)}
            alt={property.name}
            onError={(e) => { setImageError(true); e.target.src = placeholderImage; }}
          />
          <span className={`pc-status-pill pc-pill-${state}`}>
            {STATE_PILL[state]}
          </span>
        </div>

        {/* Info */}
        <div className="pc-body">
          <h3 className="pc-name">{property.name}</h3>
          <p className="pc-address">
            <FaMapMarkerAlt className="pc-pin-icon" />
            {shortAddress}
          </p>

          {/* Compliance bar */}
          {totalAssets > 0 ? (
            <div className="pc-comp">
              <div className="pc-comp-row">
                <span>Compliance</span>
                <strong>{pct}%</strong>
              </div>
              <div className="pc-comp-bar" aria-hidden="true">
                <div className="pc-comp-bar-fill" style={{ width: `${pct}%` }} />
              </div>
            </div>
          ) : (
            <div className="pc-comp pc-comp-empty">
              <span>No assets tracked yet</span>
            </div>
          )}

          {/* Asset chip row */}
          {totalAssets > 0 && (
            <div className="pc-chips">
              <span className="pc-chip">
                <strong>{totalAssets}</strong> assets
              </span>
              {failed > 0 && (
                <span className="pc-chip pc-chip-failed">
                  <strong>{failed}</strong> failed
                </span>
              )}
              {dueSoon > 0 && (
                <span className="pc-chip pc-chip-due">
                  <strong>{dueSoon}</strong> due soon
                </span>
              )}
              {untested > 0 && (
                <span className="pc-chip pc-chip-untested">
                  <strong>{untested}</strong> untested
                </span>
              )}
            </div>
          )}

          {/* Footer actions */}
          <div className="pc-footer">
            <span className="pc-footer-meta">
              View property <FaChevronRight className="pc-arrow" />
            </span>
            <button
              type="button"
              className="pc-footer-report"
              onClick={handleReport}
              title="Generate the last 30 days' compliance report"
            >
              <FaFilePdf /> Monthly Report
            </button>
          </div>
        </div>

        {/* Hover actions */}
        {showActions && (
          <div className="property-actions">
            <button type="button" className="action-button edit" onClick={handleEdit} title="Edit Property">
              <MdEdit size={16} />
            </button>
            <button type="button" className="action-button delete" onClick={handleDelete} title="Delete Property">
              <MdDelete size={16} />
            </button>
          </div>
        )}
      </div>

      {showConfirmDelete && (
        <div className="confirmation-modal" onClick={(e) => { if (e.target.className === 'confirmation-modal') setShowConfirmDelete(false); }}>
          <div className="confirmation-content">
            <h3>Delete Property</h3>
            <p>Are you sure you want to delete &ldquo;{property.name}&rdquo;? This action cannot be undone.</p>
            <div className="confirmation-actions">
              <button className="cancel-button" onClick={() => setShowConfirmDelete(false)}>Cancel</button>
              <button className="delete-button" onClick={() => { onDelete(property.id); setShowConfirmDelete(false); }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PropertyCard;
